<template>
  <fieldset
    class="vehicle-type"
    :class="{ [`vehicle-type--${selected?.toLowerCase()}`]: selected && form === 'dropdowns' }"
  >
    <legend class="vehicle-type__legend form-group__label text-body-xs-bold">
      <span v-text="$string('vehicle_finder.select_type')" />

      <abbr
        :title="$string('general.required.label')"
        v-text="$string('general.required.symbol')"
      />
    </legend>

    <div class="vehicle-type__inputs">
      <radio-input
        v-for="(type, index) of types"
        :id="getKey(type, index)"
        :key="getKey(type, index)"
        v-model="selected"
        class="vehicle-type__radio"
        autocomplete="off"
        class-label="text-body-xs-regular"
        :label="$string(`vehicle_finder.types.${type?.toLowerCase()}`)"
        :name="`${namespace}-radio`"
        required
        :value="type"
      />

      <template v-if="form === 'dropdowns'">
        <label
          class="visually-hidden"
          :for="`${namespace}-other-vehicle-types`"
          v-text="$string('vehicle_finder.dropdowns.other_vehicle_types')"
        />

        <select
          :id="`${namespace}-other-vehicle-types`"
          v-model="otherSelected"
          class="vehicle-type__other-vehicle-types text-body-xs-regular"
          autocomplete="off"
          :disabled="selected !== 'Other'"
        >
          <option
            selected
            value=""
            v-text="$string('vehicle_finder.types.other')"
          />

          <option
            v-for="(type, index) of otherVehicleTypes"
            :key="`${type.value}-${index}`"
            :value="type.value"
            v-text="type.label"
          />
        </select>
      </template>

      <input-tooltip
        class="vehicle-type__tooltip"
        :hidden="hideTooltip"
        position="left"
        :text="$string('vehicle_finder.tooltip.other_vehicle_tooltip_html')"
        :tooltip-active="tooltipActive"
        @update-tooltip="handleTooltipUpdate"
      />
    </div>
  </fieldset>
</template>

<script>

/**
 * Vue: Vehicle type (vehicle-finder-form)
 * -----------------------------------------------------------------------------
 * Radio button vehicle type select.
 * - To add a new type:
 *   - Add to types array.
 *   - Add new locale string to 'vehicle_finder.types'.
 *   - Add locale string to theme strings.
 *   - Update Liquid types string.
 *
 * @param {String} [form] - Parent form type.
 * @param {String} namespace - Unique namespace for form elements.
 * @param {String} [parentType] - Type selected in parent component.
 * @param {Boolean} [tooltipActive] - Tooltip is active.
 *
 * @emits updateSelectedType - Emit newly selected type.
 *
 */
import InputTooltip from './input-tooltip'
import RadioInput from '~async/utils/radio-input/radio-input'

import { getDefaultVehicleType, getVehicleTypes } from '~/helpers/garage'

export default {
  name: 'VehicleType',

  components: {
    InputTooltip,
    RadioInput,
  },

  props: {
    form: {
      type: String,
      default: '',
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    parentType: {
      type: String,
      default: '',
    },
    tooltipActive: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-other-selected-type': () => true,
    'update-selected-type': (payload) => payload,
    'update-tooltip': (payload) => typeof payload === 'boolean',
  },

  data() {
    return {
      otherSelected: '',
      selected: '',
      types: [],
    }
  },

  computed: {

    /**
     * Compute whether the tooltip should be hidden.
     * @returns {Boolean}
     */
    hideTooltip() {
      return !this.types.includes('Other')
    },

    /**
     * Compute 'Other Vehicle' dropdown types.
     * @returns {Array<Object>}
     */
    otherVehicleTypes() {
      if (this.form === 'vrm') {
        return []
      }

      return this.$variable('vehicleFinder.types.Other').map((type) => {
        const parts = type.split(':')

        return {
          label: parts[0]?.trim(),
          value: parts[1]?.trim(),
        }
      })
    },
  },

  watch: {

    /**
     * Watch other selected to emit update.
     */
    otherSelected() {
      if (this.form !== 'dropdowns') {
        return
      }

      this.$emit('update-other-selected-type', this.otherSelected)
    },

    /**
     * Watch selected to emit update.
     */
    selected() {
      if (!this.selected) {
        return
      }

      this.$emit('update-selected-type', this.selected)

      if (this.form === 'vrm') {
        return
      }

      this.otherSelected = ''
    },

    /**
     * Watch parent type to update local value.
     */
    parentType: {
      handler() {
        this.selected = this.parentType
      },
      immediate: true,
    },
  },

  /**
   * Create types based on form type and theme settings.
   * - Sets default type.
   */
  created() {
    this.types = getVehicleTypes(this.form)
    const defaultType = getDefaultVehicleType(this.form)

    if (!defaultType) {
      return
    }

    this.selected = defaultType
  },

  methods: {

    /**
     * Get key.
     * @param {String} type - Type of vehicle.
     * @param {Number} index - Forloop index.
     */
    getKey(type, index) {
      return `${type.toLowerCase()}-${index}-${this.namespace}`
    },

    /**
     * Handle tooltip update.
     * @param {Object} state - Tooltip state.
     */
    handleTooltipUpdate(state) {
      this.$emit('update-tooltip', state)
    },
  },
}
</script>

<style lang="scss">
@import './vehicle-type';
</style>
